import type { UsCanonicalSport } from './sports';
import { usPeriodWinner, type UsPeriod } from './sports';
/**
 * #1006 — sport and market family of a Polymarket US market, for the
 * Tracker's sport and market chips.
 *
 * US order and holding reads carry only the market slug and outcome. Like
 * international Polymarket (views/PositionTracker/trackerMarketFamily.ts,
 * which reads the live Hydra frame's `marketType`), the family comes from the
 * LIVE market source: the relay's US catalogue, fed by Hydra Core. Its
 * `marketType` is Core's `pmusMarketType` (e.g. 'moneyline'); its
 * `sportsMarketType` is the venue's own detailed name (e.g.
 * 'football_team_full_game_winner') and decides nothing here. The catalogue's
 * list/search read includes every catalogued market whatever its book state
 * (relay/src/polymarketUs/coreFeed.ts); the single-market read refuses any
 * market without a current book, so it cannot serve this.
 *
 * Same rules as international: a market the catalogue does not hold (feed
 * off, not yet published, or dropped after the game) is PENDING (null) and
 * passes every chip; a market that names an unlisted type is 'other'.
 *
 * The same lookup also keeps the market's catalogue game (canonical game key
 * and named YES team), which the Tracker's game links resolve against the
 * board.
 *
 * Lookups run one at a time — the market client lets only its latest read
 * win — and only for slugs a host asked about: the Tracker asks for every
 * market in its US read, whatever its chips hide. A market found is never
 * looked up again. A miss is asked again by a later request once
 * `US_MARKET_MISS_RETRY_MS` has passed, so a feed that comes up later is
 * noticed without re-asking on every render.
 */
import type { UsDiscoveryMarket, UsMarketClient } from './marketClient';
import type { UsScope } from './types';
import { usGamePropTarget, type UsGamePropTarget } from './gamePropTarget';
import { usPlayerTarget, type UsPlayerTarget } from './playerTarget';
import { usFullGameTotalTarget, type UsFullGameTotalTarget } from './totalTarget';

/** Structurally the Tracker's `TrackerMarketFamily`; kept here so this module
 * stays free of browser stores. */
export type UsMarketFamily = 'moneyline' | 'spread' | 'total' | 'props' | 'other';
/** The Tracker's sport ids for the only sports the US catalogue carries. */
export type UsMarketSport = UsCanonicalSport;
export type UsMarketClass = { sport: UsMarketSport | null; family: UsMarketFamily };
/** The catalogue's game identity for a market (#1006 game links): the
 * producer's canonical game key and named YES team, exactly as listed. */
export type UsMarketGame = { gameKey: string; yesTeam: string | null; family: UsMarketFamily;
  /** Exact supported period winner; family remains the venue's props chip. */
  periodWinner?: Exclude<UsPeriod, 'full'>;
  gamePropTarget?: UsGamePropTarget; playerTarget?: UsPlayerTarget;
  /** #1030 — the exact supported native full-game total (slug, family, line). */
  totalTarget?: UsFullGameTotalTarget };
export type UsLookupAccount = { accountId: string; scope: UsScope };

export const US_MARKET_MISS_RETRY_MS = 5 * 60_000;


/** International Polymarket's `marketType` table (trackerMarketFamily.ts),
 * with the plural spellings accepted too. Only 'moneyline' has been observed
 * from Core so far (relay fixtures); the others follow the same vocabulary. */
const FAMILY: Readonly<Record<string, UsMarketFamily>> = {
  moneyline: 'moneyline',
  game: 'moneyline',
  spread: 'spread',
  spreads: 'spread',
  total: 'total',
  totals: 'total',
  prop: 'props',
  props: 'props',
};

/** The catalogue's `marketType`. Present but unlisted is 'other'. */
export function usMarketFamily(marketType: string): UsMarketFamily {
  return FAMILY[marketType.trim().toLowerCase()] ?? 'other';
}

/** A catalogue market's sport and family: the one rule the Tracker's chips
 * and Org Detail's sport filter (#1202) both classify by. */
export function usMarketClass(market: Pick<UsDiscoveryMarket, 'sport' | 'marketType'>): UsMarketClass {
  return { sport: market.sport.toLowerCase() as UsMarketSport, family: usMarketFamily(market.marketType) };
}

/** A miss says why: `absent` = the catalogue answered and does not list this
 * exact slug; `failed` = the read threw, or the slug was not on the one page a
 * search returns while more pages exist, so the answer is not known. */
type Entry = { cls: UsMarketClass; game: UsMarketGame | null } | { missAtMs: number; reason: 'absent' | 'failed' };

/** #1232 — one slug's lookup as a host that must not guess reads it. The board's
 * activity filters fail open on `pending` and `failed`, and treat `absent` as
 * "no live US market". `get`/`game` keep folding all three into null. */
export type UsMarketLookup =
  | { state: 'pending' }
  | { state: 'failed' }
  | { state: 'absent' }
  | { state: 'placed'; game: UsMarketGame | null };

export class UsMarketClassifier {
  private readonly entries = new Map<string, Entry>();
  private readonly queue: { slug: string; account: UsLookupAccount }[] = [];
  private readonly queued = new Set<string>();
  private running = false;
  private closed = false;
  private version = 0;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly client: Pick<UsMarketClient, 'setSelection' | 'listMarkets' | 'invalidate'>,
    private readonly clock: () => number = Date.now,
  ) {}

  /** null = pending (not looked up yet, or the catalogue does not hold it). */
  get(slug: string): UsMarketClass | null {
    const entry = this.entries.get(slug);
    return entry && 'cls' in entry ? entry.cls : null;
  }

  /** The market's catalogue game, from the same lookup; null while pending. */
  game(slug: string): UsMarketGame | null {
    const entry = this.entries.get(slug);
    return entry && 'cls' in entry ? entry.game : null;
  }

  /** The lookup's state; never asked, queued and in flight are all `pending`. */
  lookup(slug: string): UsMarketLookup {
    const entry = this.entries.get(slug);
    if (!entry) return { state: 'pending' };
    return 'cls' in entry ? { state: 'placed', game: entry.game } : { state: entry.reason };
  }

  getVersion = (): number => this.version;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /** Queue lookups for slugs with no answer (or a miss older than the retry). */
  request(slugs: Iterable<string>, account: UsLookupAccount): void {
    if (this.closed) return;
    const now = this.clock();
    for (const slug of slugs) {
      const entry = this.entries.get(slug);
      if (this.queued.has(slug) || (entry && ('cls' in entry || now - entry.missAtMs < US_MARKET_MISS_RETRY_MS))) continue;
      this.queued.add(slug);
      this.queue.push({ slug, account });
    }
    void this.pump();
  }

  /** Mounted (again): StrictMode's replayed unmount must not leave it shut. */
  open(): void {
    this.closed = false;
  }

  /** The session ended: drop queued work; a late answer is ignored. */
  close(): void {
    this.closed = true;
    this.queue.length = 0;
    this.queued.clear();
    this.client.invalidate();
  }

  private async pump(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      while (!this.closed && this.queue.length > 0) {
        const { slug, account } = this.queue.shift()!;
        let entry: Entry = { missAtMs: this.clock(), reason: 'failed' };
        try {
          this.client.setSelection(account);
          const page = await this.client.listMarkets({ q: slug });
          // Search is a substring match; only the exact slug classifies.
          const market = page.markets.find((m) => m.marketSlug === slug);
          // Not on this page, and no more pages: the catalogue does not list it.
          if (!market && page.nextCursor === null) entry = { missAtMs: entry.missAtMs, reason: 'absent' };
          if (market) {
            const cls = usMarketClass(market);
            const family = cls.family;
            const periodWinner = market.marketType === 'props' ? usPeriodWinner(market.sportsMarketType, market.sport) : null;
            entry = {
              cls,
              game: typeof market.gameKey === 'string' && market.gameKey
                ? { gameKey: market.gameKey, yesTeam: market.yesTeam ?? null, family,
                  ...(periodWinner && market.period === periodWinner ? { periodWinner } : {}),
                  ...(usGamePropTarget(market) ? { gamePropTarget: usGamePropTarget(market)! } : {}),
                  ...(usPlayerTarget(market) ? { playerTarget: usPlayerTarget(market)! } : {}),
                  ...(usFullGameTotalTarget(market) ? { totalTarget: usFullGameTotalTarget(market)! } : {}) } : null,
            };
          }
        } catch {
          // Catalogue unavailable or the read was refused: pending, retried later.
        }
        this.queued.delete(slug);
        if (this.closed) return;
        this.entries.set(slug, entry);
        // A miss notifies too (#1232): a host waiting on the answer must see it.
        this.version++;
        for (const listener of this.listeners) listener();
      }
    } finally {
      this.running = false;
    }
  }
}
